import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';

import * as XLSX from 'xlsx';
import * as _ from 'lodash';

import {ImportSysPropStructure} from '../../../main/models/import-sys-props';

type AOA = any[][];

interface Indexer {
  [p: string]: string;
}

@Component({
  selector: 'app-import-from-excel',
  templateUrl: './import-from-excel.component.html',
  styleUrls: ['./import-from-excel.component.scss']
})
export class ImportFromExcelComponent implements OnInit {

  data: AOA = [];
  tableTitles: string[] = [];
  tableTitleItems: {title: string, disabled: boolean}[] = [];
  formattedItems: any[] = [];
  remainingItems: any[] = [];
  isValidating: boolean;
  file: File;

  invalidRows: Indexer[];
  validRows: Indexer[];

  @Input() propertiesMapping: ImportSysPropStructure[];
  @Input() mainProp: string;
  @Input() maxItems: number;

  @Input() customValidator: (rowItem: Indexer) => void;
  @Input() customSeparator: (formattedItems: any[]) => any[];

  @Output() confirmImport: EventEmitter<any> = new EventEmitter();
  @Output() cancelImport: EventEmitter<any> = new EventEmitter();

  constructor() { }

  ngOnInit() {}

  onFileChange(evt: any) {
    /* wire up file reader */
    const target: DataTransfer = <DataTransfer>(evt.target);
    if (target.files.length !== 1) {
      throw new Error('Cannot use multiple files');
    }
    this.reset();

    const reader: FileReader = new FileReader();
    this.file = target.files[0];

    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      /* save data */
      this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 })).filter(item => !_.isEmpty(item));
      this.tableTitles = this.data[0];
      this.tableTitleItems = this.tableTitles.map(title => {
        return {
          title: title,
          disabled: this.propertiesMapping.some(prop => {
            if (prop.label === title) {
              prop.colName = title;

              return true;
            }

            return false;
          }),
        };
      });
    };
    reader.readAsBinaryString(target.files[0]);
  }

  exportInvalidItems(): void {
    /* generate worksheet */
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.invalidRows);

    /* generate workbook and add the worksheet */
    const wb: XLSX.WorkBook = { Sheets: { 'data': ws }, SheetNames: ['data'] };
    XLSX.utils.book_append_sheet(wb, ws, `Sheet1`);

    /* save to file */
    XLSX.writeFile(wb, `invalid_rows_${this.file.name}`);
  }

  selectProp() {
    this.tableTitleItems = this.tableTitleItems.map(title => {
      return {
        ...title,
        disabled: this.propertiesMapping.some(property => {
          return (property.colName === title.title);
        }),
      };
    });

    this.resetRowsValidation();
  }

  isValidCol(prop: ImportSysPropStructure) {
    prop.isInvalidCol = false;
    if (prop.colName && prop.dependingProp) {
      const dependingProp = this.propertiesMapping.filter(property => property.label === prop.dependingProp)[0];
      prop.isInvalidCol = !dependingProp.colName;
      return dependingProp.colName ? 'Matched' : `${prop.label} is depending on ${dependingProp.label}`;
    }

    if (prop.colName) {
      return 'Matched';
    }

    if (prop.isRequired) {
      prop.isInvalidCol = true;
      return 'Required';
    }

    return 'Optional';
  }

  validateData() {
    this.isValidating = true;
    const data = this.data.slice(1).map(row => {
      if (row.length < this.tableTitles.length) {
        for (let i = this.tableTitles.length - row.length; i > 0; i--) {
          row.push(undefined);
        }
      }

      return row;
    });

    const transformed: Indexer[] = [];
    _.reduce(data, (transformedResult, rowData) => {
      const item: Indexer = {};
      _.reduce(rowData, (resultItem: Indexer, value: string, index: number) => {
        const propMapping = this.propertiesMapping
          .find(prop => prop.colName === this.tableTitles[index]);

        if (propMapping) {
          resultItem[propMapping.colName] = value;
        }

        return resultItem;
      }, item);
      transformedResult.push(item);

      return transformedResult;
    }, transformed);

    const groupedItems: {[p: string]: Indexer[]} = this.mainProp ? _.groupBy(transformed, this.propertiesMapping
      .find(prop => prop.propName === this.mainProp).colName) : _.reduce(transformed, (result, item, i) => {
        result[i] = [item];
        return result;
    }, {});

    const formattedItems: any[] = [];

    _.map(groupedItems, (groupedItemValues: Indexer[]) => {
      const formattedItem: any = {};
      groupedItemValues.forEach((groupedItem, groupedIndex) => {
        _.reduce(groupedItem, (
          formattedItemResult: any,
          groupedItemValue: string,
          groupedItemKey: string,
        ) => {
          const propMapping = this.propertiesMapping
            .find(prop => prop.colName === groupedItemKey);

          if (propMapping) {
            if (!propMapping.pattern.test(_.isUndefined(groupedItemValue) ? '' : groupedItemValue)) {
              groupedItem.invalidMsg = groupedItem.invalidMsg ?
                groupedItem.invalidMsg + `\nInvalid ${groupedItemKey}` : `Invalid ${groupedItemKey}`;
            }

            if (propMapping.isRequired && !groupedItemValue) {
              groupedItem.invalidMsg = groupedItem.invalidMsg ?
                groupedItem.invalidMsg + `\n${groupedItemKey} is required` : `${groupedItemKey} is required`;
            }

            if (groupedItemValue && !groupedItem.invalidMsg) {
              const parsedPath = propMapping.propName.replace('[i]', `[${groupedIndex.toString()}]`);

              _.set(formattedItemResult, parsedPath, String(groupedItemValue));
            }
          }

          return formattedItemResult;
        }, formattedItem);

        if (this.customValidator) {
          this.customValidator(groupedItem);
        }

        if (groupedItem.invalidMsg) {
          _.reduce(groupedItem, (
            formattedItemResult: Indexer,
            groupedItemValue: string,
            groupedItemKey: string
          ) => {
            const propMapping = this.propertiesMapping
              .find(prop => prop.colName === groupedItemKey);

            if (propMapping) {
              const parsedPath = propMapping.propName.replace('[i]', `[${groupedIndex.toString()}]`);

              _.unset(formattedItemResult, parsedPath);
            }

            return formattedItemResult;
          }, formattedItem);
        }
      });
      formattedItems.push(formattedItem);
    });

    if (this.customSeparator) {
      this.formattedItems = this
        .customSeparator(this.removeEmptyProperties(JSON.parse(JSON.stringify(formattedItems))));
    } else {
      this.formattedItems = this.removeEmptyProperties(JSON.parse(JSON.stringify(formattedItems)));
    }

    const validatedRecords = _.flatMap(groupedItems);
    this.invalidRows = validatedRecords.filter(row => row.invalidMsg);
    this.validRows = validatedRecords.filter(row => !row.invalidMsg);
    this.isValidating = false;
    this.remainingItems = this.formattedItems;
    if (this.maxItems && this.remainingItems.length > this.maxItems) {
      this.remainingItems = this.remainingItems.slice(0, this.maxItems);
    }

    window.scroll(0, 200);
  }

  hasOneItemInvalid() {
    return this.propertiesMapping.some(prop => prop.isInvalidCol);
  }

  removeEmptyProperties(obj: any): any {
    if (_.isObject(obj) && _.isEmpty(obj)) {
      return undefined;
    }

    if (_.isArray(obj)) {
      const filteredArray = (<any[]>obj).map(item => {
        if (_.isObject(item) && _.isEmpty(item)) {
          return undefined;
        }

        if (_.isObject(item)) {
          const validatedItem = this.removeEmptyProperties(item);
          return _.isEmpty(validatedItem) ? undefined : validatedItem;
        }

        return item;
      });
      return _.compact(filteredArray);
    }

    if (_.isObject(obj)) {
      _.map(obj, (value, key) => {
        if (_.isObject(value) && _.isEmpty(value)) {
          delete obj[key];
          return;
        }

        if (_.isObject(value) || _.isArray(value)) {
          const validatedProp = this.removeEmptyProperties(value);
          _.assign(obj, { [key]: validatedProp });
          if (_.isEmpty(validatedProp)) {
            delete obj[key];
          }
        }
      });
    }

    return obj;
  }

  reset() {
    this.resetFileUploading();
    this.resetPropMapping();
    this.resetRowsValidation();
  }

  resetFileUploading() {
    this.data = [];
    this.file = null;
  }

  resetPropMapping() {
    this.propertiesMapping = this.propertiesMapping.map(prop => ({
      ...prop,
      colName: '',
    }));

    this.tableTitles = [];
    this.tableTitleItems = [];
  }

  resetRowsValidation() {
    this.invalidRows = [];
    this.validRows = [];
    this.formattedItems = [];
  }

  submitImported() {
    this.confirmImport.emit({
      file: this.file,
      formattedItems: this.remainingItems,
    });
  }

  cancel() {
    this.cancelImport.emit();
  }
}
